import { combineReducers } from "redux";
import { ActionSchema } from "../actions";
import authReducer, { AuthSchema } from "./authReducer";
import appStateReducer, { AppStateSchema } from "./appStateReducer";
import apiMetricsReducer, { ApiMetricsSchema } from "./apiMetricsReducer";
import insightsReducer, { InsightsSchema } from "./insightsReducer";
import billingReducer, { BillingSchema } from "./billingReducer";
import deploymentReducer, { DeploymentSchema } from "./deploymentReducer";
import teamReducer, { TeamSchema } from "./teamReducer";
import integrationsReducer, { IntegrationsSchema } from "./integrationsReducer";
import supportReducer, { SupportSchema } from "./supportReducer";
import dataConfigurationReducer, { DataConfigurationSchema } from "./dataConfigurationReducer";

import { securityConfigurationReducer, SecurityConfigurationSchema } from "./securityConfigurationReducer";

import AccountCombineReducer, { ApiAccountSchema } from "./accountReducer";
import customerManagementReducer, { CustomerManagementSchema } from "./customerManagementReducer";
import segmentationsReducer, { SegmentationsSchema } from "./segmentationReducer";
import platformConfigurationReducer, { PlatformConfigurationSchema } from "./platformConfigurationReducer";
import analyticsReducer, { AnalyticsState } from "./analyticsReducer";

import workflowReducer, { WorkflowSchema } from "./workflowReducer";
import passkeyReducer, { NewPasskeyAuthenticationSchema } from "../features/authentication/passkeys";
import passwordlessLoginSettingsReducer, { PasswordlessLoginSettingsSchema } from "../features/authentication/passwordlessLoginSettings";
import trialLimitReducer, { TrialPlanLimitSchema } from "../features/trialLimit/trialLimit";
import domainAccessManagementReducer, { DomainAccessManagementState } from "../features/security/platformSecurity/domainAccessManagement";
import { OrganizationSchema } from "../features/organizations/organization";
import organizationReducer from "../features/organizations";
import domainAccessRestrictionReducer, { DomainAccessRestrictionState } from "../features/security/platformSecurity/domainAccessRestriction";
import hostedPagesReducer, { NewHostedPagesSchema } from "../features/hostedPages";
import customDomainMappingReducer, { CustomDomainMappingSchema } from "../features/customDomainTemplateMapping";
import oauthClientsReducer, { OAuthClientsState } from "../features/oauthClientsSlice";
import userConnectionsReducer, { UserConnectionsState } from "../features/userConnectionsSlice";
import onboardingReducer, { OnboardingState } from "../features/onboarding/onboarding";
import signingKeysReducer, { SigningKeysState } from "../features/settings/signingKeys";
import securityHeadersReducer, { SecurityHeadersState } from "../features/settings/securityHeaders";
// Default reducer
function defaultReducer(state = {}, _action: ActionSchema<any>) {
  return state;
}

// Combine all schemas
export interface RootSchema {
  default: {};
  appState: AppStateSchema;
  auth: AuthSchema;
  apiMetrics: ApiMetricsSchema;
  insights: InsightsSchema;
  billing: BillingSchema;
  team: TeamSchema;
  account: ApiAccountSchema;
  deployment: DeploymentSchema;
  integrations: IntegrationsSchema;
  support: SupportSchema;
  securityConfiguration: SecurityConfigurationSchema;
  platformConfiguration: PlatformConfigurationSchema;
  dataConfiguration: DataConfigurationSchema;
  customerManagement: CustomerManagementSchema;
  segmentations: SegmentationsSchema;
  workflow: WorkflowSchema;
  passkey: NewPasskeyAuthenticationSchema;
  passwordlessLoginSettings: PasswordlessLoginSettingsSchema;
  organizations: OrganizationSchema;
  trialLimits: TrialPlanLimitSchema;
  domainAccessManagement: DomainAccessManagementState;
  domainAccessRestriction: DomainAccessRestrictionState;
  hostedPages: NewHostedPagesSchema;
  customDomainMapping: CustomDomainMappingSchema;
  oauthClients: OAuthClientsState;
  userConnections: UserConnectionsState;
  onboarding: OnboardingState;
  analytics: AnalyticsState;
  signingKeys: SigningKeysState;
  securityHeaders: SecurityHeadersState;
}

// Combine all reducers
const rootReducer = combineReducers({
  default: defaultReducer,
  appState: appStateReducer,
  auth: authReducer,
  apiMetrics: apiMetricsReducer,
  insights: insightsReducer,
  team: teamReducer,
  billing: billingReducer,
  account: AccountCombineReducer,
  deployment: deploymentReducer,
  integrations: integrationsReducer,
  support: supportReducer,
  securityConfiguration: securityConfigurationReducer,
  platformConfiguration: platformConfigurationReducer,
  customerManagement: customerManagementReducer,
  dataConfiguration: dataConfigurationReducer,
  segmentations: segmentationsReducer,
  workflow: workflowReducer,
  passkey: passkeyReducer,
  passwordlessLoginSettings: passwordlessLoginSettingsReducer,
  trialLimits: trialLimitReducer,
  organizations: organizationReducer,
  domainAccessManagement: domainAccessManagementReducer,
  domainAccessRestriction: domainAccessRestrictionReducer,
  hostedPages: hostedPagesReducer,
  customDomainMapping: customDomainMappingReducer,
  oauthClients: oauthClientsReducer,
  userConnections: userConnectionsReducer,
  onboarding: onboardingReducer,
  signingKeys: signingKeysReducer,
  securityHeaders: securityHeadersReducer,
  analytics: analyticsReducer
});

export default rootReducer;
